// admin/src/pages/location/LocationTracking.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { debounce } from "lodash";
import {
  Container, Row, Col, Button, Modal, Alert, FormControl,
  InputGroup, Dropdown, Badge, Spinner,
} from "react-bootstrap";
import api from "../../services/api";
import { Colors } from "../../constants/Colors";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

/* ---------------- Design tokens ---------------- */
const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover:
    "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

/* ---------------- Map constants ---------------- */
const MAP_CONTAINER_STYLE = { width: "100%", height: "520px" };
const DEFAULT_CENTER = { lat: 13.5833, lng: 123.3167 };
const DEFAULT_ZOOM = 11;
const MAX_ZOOM_AFTER_FIT = 16;

const RINCONADA_BOUNDS = {
  minLat: 13.25,
  maxLat: 13.65,
  minLng: 123.15,
  maxLng: 123.65,
};

/* ---------------- Marker colors (Google Maps needs hex) ---------- */
const MARKER_COLORS = {
  Active: "#4caf50",
  Busy: "#ff9800",
  Inactive: "#9c27b0",
  Offline: "#f44336",
  pending: "#f59e0b",
  rejected: "#ef4444",
  suspended: "#6b7280",
  default: "#2196f3",
};

/* ---------------- Status meta (for pills) ---------------- */
const STATUS_META = {
  active: {
    color: Colors.SUCCESS,
    bg: `${Colors.SUCCESS}1f`,
    label: "Active",
  },
  busy: {
    color: Colors.WARNING,
    bg: `${Colors.WARNING}1f`,
    label: "Busy",
  },
  offline: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Offline" },
  inactive: {
    color: Colors.GRAY,
    bg: `${Colors.GRAY}26`,
    label: "Inactive",
  },
  pending: {
    color: Colors.WARNING,
    bg: `${Colors.WARNING}1f`,
    label: "Pending",
  },
  rejected: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Rejected" },
  suspended: {
    color: Colors.GRAY,
    bg: `${Colors.GRAY}26`,
    label: "Suspended",
  },
};

const STATUS_FILTER_OPTIONS = ["All", "Active", "Busy", "Inactive", "Offline"];

const LEGEND_ITEMS = ["Active", "Busy", "Inactive", "Offline"];

/* Retry config for fetch */
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 3000, 5000];
const AUTO_REFRESH_INTERVAL = 60000;
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24h
const PER_PAGE = 200;

/* Stable callback name — MUST match the one baked into the script URL */
const GOOGLE_MAPS_CALLBACK = "initGoogleMapsForLocationTracking";
const GOOGLE_MAPS_SCRIPT_ID = "google-maps-script";

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const getStatusMeta = (status) =>
  STATUS_META[(status || "").toLowerCase()] || {
    color: Colors.GRAY,
    bg: `${Colors.GRAY}26`,
    label: status || "Unknown",
  };

const getInitials = (op) =>
  `${(op.first_name?.[0] || "").toUpperCase()}${(
    op.last_name?.[0] || ""
  ).toUpperCase()}` || "?";

const isInsideRinconada = (lat, lng) =>
  lat >= RINCONADA_BOUNDS.minLat &&
  lat <= RINCONADA_BOUNDS.maxLat &&
  lng >= RINCONADA_BOUNDS.minLng &&
  lng <= RINCONADA_BOUNDS.maxLng;

const isLocationStale = (lastUpdate, now) =>
  !!lastUpdate && new Date(lastUpdate) < new Date(now - STALE_THRESHOLD_MS);

/* Normalize a raw operator record from the API */
const enrichOperator = (raw, now) => {
  const lat = raw.latitude ? parseFloat(raw.latitude) : null;
  const lng = raw.longitude ? parseFloat(raw.longitude) : null;
  const hasLocation = lat !== null && lng !== null;

  return {
    ...raw,
    latitude: lat,
    longitude: lng,
    hasLocation,
    isStale: isLocationStale(raw.last_location_update, now),
    isOutsideBounds: hasLocation && !isInsideRinconada(lat, lng),
  };
};

/* Build the InfoWindow HTML for a marker */
const buildMarkerInfoHtml = (op) => {
  const color = MARKER_COLORS[op.status] || MARKER_COLORS.default;
  return `
    <div style="padding: 8px; min-width: 200px; font-family: system-ui;">
      <h4 style="margin: 0 0 6px 0;">${op.first_name || ""} ${op.last_name || ""}</h4>
      <p style="margin: 0 0 4px 0;"><strong>Status:</strong>
        <span style="color:${color}; font-weight:600;">${op.status || "unknown"}</span>
      </p>
      ${
        op.email
          ? `<p style="margin: 0 0 4px 0;"><strong>Email:</strong> ${op.email}</p>`
          : ""
      }
      ${
        op.phone
          ? `<p style="margin: 0 0 4px 0;"><strong>Phone:</strong> ${op.phone}</p>`
          : ""
      }
      ${
        op.location
          ? `<p style="margin: 0 0 4px 0;"><strong>Location:</strong> ${op.location}</p>`
          : ""
      }
      ${
        op.last_location_update
          ? `<p style="margin: 0 0 4px 0; font-size:12px; color:#666;">Updated: ${new Date(
              op.last_location_update
            ).toLocaleString()}</p>`
          : ""
      }
      ${
        op.isStale
          ? `<p style="margin: 4px 0 0 0; color: orange;"><small>⚠️ Location data may be stale</small></p>`
          : ""
      }
      ${
        op.isOutsideBounds
          ? `<p style="margin: 4px 0 0 0; color: #f44336;"><small>📍 Outside Rinconada bounds</small></p>`
          : ""
      }
    </div>
  `;
};

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    /* ---- Page shell ---- */
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }

    /* ---- Header ---- */
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }

    /* ---- Card ---- */
    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    /* ---- Toolbar & inputs ---- */
    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 14px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-input { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; color: ${Colors.TEXT_DARK}; height: auto !important; }
    .bk-input:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-input-icon { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-right: none !important; border-radius: ${T.radiusSm} 0 0 ${T.radiusSm} !important; color: ${Colors.GRAY}; }
    .bk-filter-btn { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; text-align: left; display: flex; align-items: center; justify-content: space-between; height: auto !important; min-height: 42px; }
    .bk-filter-btn:hover, .bk-filter-btn:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: none !important; color: ${Colors.TEXT_DARK} !important; }
    .bk-filter-btn::after { margin-left: auto; }

    /* ---- Pills & chips ---- */
    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
    .bk-pill.no-dot::before { display: none; }

    .bk-chip { display: inline-flex; align-items: center; padding: 5px 11px; border-radius: 8px; font-size: 12px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_DARK}; cursor: pointer; border: 1px solid transparent; transition: all .15s; user-select: none; }
    .bk-chip:hover { border-color: ${Colors.PRIMARY}; color: ${Colors.PRIMARY}; }
    .bk-chip.active { background: ${Colors.PRIMARY}; color: #fff; border-color: ${Colors.PRIMARY}; }

    .bk-legend-item { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_DARK}; }
    .bk-legend-dot { width: 10px; height: 10px; border-radius: 50%; }

    /* ---- Empty state ---- */
    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    /* ---- Skeleton ---- */
    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    /* ---- Buttons ---- */
    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-primary:hover { background: #e87a2e !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }
    .bk-icon-btn { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; width: 36px; height: 36px; border-radius: 10px !important; padding: 0 !important; display: inline-grid !important; place-items: center; transition: all .18s; }
    .bk-icon-btn:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }
    .bk-icon-btn.active { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }

    /* ---- Map wrap ---- */
    .bk-map-wrap { position: relative; width: 100%; height: 520px; border-radius: ${T.radiusSm}; overflow: hidden; border: ${T.border}; background: ${Colors.LIGHT_GRAY}; }
    .bk-map-overlay { position: absolute; top: 12px; left: 12px; right: 12px; background: rgba(255,255,255,.95); padding: 12px 16px; border-radius: 10px; box-shadow: ${T.shadow}; z-index: 2; }
    .bk-map-placeholder { position: absolute; inset: 0; display: grid; place-items: center; background: ${Colors.LIGHT_GRAY}; z-index: 1; }

    /* ---- Operator cards ---- */
    .bk-operator-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 16px; transition: transform .18s ease, box-shadow .18s ease; height: 100%; }
    .bk-operator-card:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-operator-name { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 14.5px; }
    .bk-operator-meta { font-size: 12px; color: ${Colors.TEXT_LIGHT}; margin-top: 3px; display: flex; align-items: center; }
    .bk-avatar { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 14px; display: grid; place-items: center; flex-shrink: 0; }

    /* ---- Modal ---- */
    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    /* ---- Dropdown ---- */
    .dropdown-menu { border-radius: ${T.radiusSm}; border: ${T.border}; box-shadow: ${T.shadow}; padding: 6px; }
    .dropdown-item { border-radius: 6px; padding: 8px 12px; font-size: 13px; }
    .dropdown-item.active { background: ${Colors.PRIMARY}1a !important; color: ${Colors.TEXT_DARK} !important; }

    /* ---- Spin ---- */
    .spin { animation: bk-spin 1s linear infinite; display: inline-block; }
    @keyframes bk-spin { to { transform: rotate(360deg); } }
  `}</style>
);

/* ================================================================== */
/* PRESENTATIONAL COMPONENTS                                          */
/* ================================================================== */

const StatusPill = ({ status }) => {
  const m = getStatusMeta(status);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

const OperatorAvatar = ({ operator }) => (
  <div className="bk-avatar">{getInitials(operator)}</div>
);

const WarningPill = ({ icon, label, color }) => (
  <span
    className="bk-pill no-dot"
    style={{
      color,
      background: `${color}1f`,
      fontSize: 11,
    }}
  >
    <i className={`bi ${icon} me-1`} />
    {label}
  </span>
);

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const LocationTracking = () => {
  /* ---------------- Data state ---------------- */
  const [operators, setOperators] = useState([]);
  const [filteredOperators, setFilteredOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [retryStatus, setRetryStatus] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [staleLocationAlert, setStaleLocationAlert] = useState(false);

  /* ---------------- Filters ---------------- */
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showOffline, setShowOffline] = useState(true);
  const [showWithoutLocation, setShowWithoutLocation] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  /* ---------------- Map state ---------------- */
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [map, setMap] = useState(null);
  const [mapMarkers, setMapMarkers] = useState([]);

  /* ---------------- Directions modal ---------------- */
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [selectedForDirections, setSelectedForDirections] = useState(null);

  /* ---------------- Refs ---------------- */
  const refreshIntervalRef = useRef(null);
  const mapRef = useRef();
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  /* ================================================================ */
  /* DATA FETCHING                                                    */
  /* ================================================================ */

  const fetchOperators = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setRetryStatus(null);

      /* Paginate through all operators */
      let allOperators = [];
      let currentPage = 1;
      let lastPage = 1;

            do {
        const response = await api.get("/admin/operators", {
          params: { per_page: PER_PAGE, page: currentPage },
        });

        // Laravel paginator shape: { data: [...], last_page: N, total: N, ... }
        if (Array.isArray(response.data?.data)) {
          allOperators = [...allOperators, ...response.data.data];
          lastPage = response.data.last_page ?? 1;
          currentPage++;
        } else {
          throw new Error(
            response.data?.message || "Failed to fetch operator locations"
          );
        }
      } while (currentPage <= lastPage);

      /* Enrich each operator with derived fields */
      const now = new Date();
      setLastFetchTime(now);

      const enriched = allOperators.map((op) => enrichOperator(op, now));
      const hasStale = enriched.some((op) => op.isStale);

      setStaleLocationAlert(hasStale);
      setOperators(enriched);
      setRetryCount(0);
    } catch (err) {
      console.error("Error fetching operators:", err);
      console.error("→ status:", err?.response?.status);
      console.error("→ data:", err?.response?.data);
      console.error("→ message:", err?.message);

      if (retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAYS[retryCount];
        setRetryStatus(`Retrying… (${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => setRetryCount(retryCount + 1), delay);
      } else {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch operator locations after multiple attempts"
        );
        setRetryStatus(null);
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  /* ================================================================ */
  /* FILTERS                                                          */
  /* ================================================================ */

  const applyFilters = useCallback(
    (operatorsData, status, showOfflineFlag, showNoLocationFlag, search) => {
      let filtered = [...operatorsData];

      /* Search */
      if (search && search.trim()) {
        const q = search.trim().toLowerCase();
        filtered = filtered.filter((op) => {
          const fullName = `${op.first_name || ""} ${
            op.last_name || ""
          }`.toLowerCase();
          return (
            fullName.includes(q) ||
            (op.email || "").toLowerCase().includes(q) ||
            (op.phone || "").toLowerCase().includes(q)
          );
        });
      }

      /* Status */
      if (status !== "All") {
        filtered = filtered.filter(
          (op) => (op.status || "").toLowerCase() === status.toLowerCase()
        );
      }

      /* Hide offline / stale */
      if (!showOfflineFlag) {
        filtered = filtered.filter((op) => {
          const s = (op.status || "").toLowerCase();
          return s !== "offline" && s !== "inactive" && !op.isStale;
        });
      }

      /* Hide without-location */
      if (!showNoLocationFlag) {
        filtered = filtered.filter((op) => op.hasLocation);
      }

      setFilteredOperators(filtered);
    },
    []
  );

  /* ================================================================ */
  /* EFFECTS                                                          */
  /* ================================================================ */

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  /* Auto-refresh interval */
  useEffect(() => {
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(
        fetchOperators,
        AUTO_REFRESH_INTERVAL
      );
    } else if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [autoRefresh, fetchOperators]);

  /* Reapply filters whenever source data or filter inputs change */
  useEffect(() => {
    applyFilters(
      operators,
      statusFilter,
      showOffline,
      showWithoutLocation,
      searchQuery
    );
  }, [
    operators,
    statusFilter,
    showOffline,
    showWithoutLocation,
    searchQuery,
    applyFilters,
  ]);

  /* ================================================================ */
  /* GOOGLE MAPS SCRIPT LOADER                                        */
  /*                                                                  */
  /* Uses a STABLE callback name so that:                             */
  /*  - The URL callback always matches window[callback]              */
  /*  - React StrictMode's double-mount doesn't delete the callback   */
  /*    before Google Maps can invoke it                              */
  /* ================================================================ */

  useEffect(() => {
    // Already loaded in a previous mount — nothing to do
    if (window.google && window.google.maps) {
      setGoogleMapsLoaded(true);
      return;
    }

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setError("Google Maps API key is missing. Please check your .env file.");
      return;
    }

    const handleLoaded = () => {
      if (window.google && window.google.maps) {
        console.log("Google Maps API loaded successfully");
        setGoogleMapsLoaded(true);
      }
    };

    // Register the stable callback globally. Overwriting is safe and
    // idempotent — same function reference every mount.
    window[GOOGLE_MAPS_CALLBACK] = handleLoaded;

    let script = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (script) {
      // Script tag already in the DOM (e.g. StrictMode remount)
      if (window.google && window.google.maps) {
        handleLoaded();
      } else {
        script.addEventListener("load", handleLoaded);
        script.addEventListener("error", () => {
          setError("Failed to load Google Maps API.");
        });
      }

      return () => {
        script?.removeEventListener("load", handleLoaded);
        // NOTE: intentionally NOT deleting window[GOOGLE_MAPS_CALLBACK]
        // here — the still-loading script might call it after cleanup.
      };
    }

    // First mount — inject the script
    script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&loading=async&callback=${GOOGLE_MAPS_CALLBACK}`;
    script.async = true;
    script.defer = true;

    script.onerror = (err) => {
      console.error("Failed to load Google Maps API:", err);
      setError(
        "Failed to load Google Maps API. Please check your internet connection and API key."
      );
      setGoogleMapsLoaded(false);
    };

    document.head.appendChild(script);

    return () => {
      // Do NOT remove the script or delete the callback on unmount.
      // React 18 StrictMode in dev will double-mount; removing here
      // causes the "callback is not a function" race condition.
    };
  }, []);

  /* ================================================================ */
  /* MAP INIT                                                         */
  /* ================================================================ */

  const initMap = useCallback(() => {
    if (
      !googleMapsLoaded ||
      !window.google ||
      !window.google.maps ||
      !mapRef.current
    )
      return;

    try {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        mapId: "operator_tracking_map",
      });

      setMap(newMap);
      infoWindowRef.current = new window.google.maps.InfoWindow();

      /* Clear previous markers */
      markersRef.current.forEach((m) => {
        if (m && typeof m.setMap === "function") m.setMap(null);
      });
      markersRef.current = [];

      const operatorsWithLocation = filteredOperators.filter(
        (op) => op.hasLocation
      );

      if (operatorsWithLocation.length === 0) {
        newMap.setCenter(DEFAULT_CENTER);
        newMap.setZoom(DEFAULT_ZOOM);
        setMapMarkers([]);
        return;
      }

      const useAdvancedMarkers =
        window.google.maps.marker &&
        window.google.maps.marker.AdvancedMarkerElement;

      const bounds = new window.google.maps.LatLngBounds();

      operatorsWithLocation.forEach((op) => {
        const position = { lat: op.latitude, lng: op.longitude };
        bounds.extend(position);

        const statusColor =
          MARKER_COLORS[op.status] || MARKER_COLORS.default;
        const infoContent = buildMarkerInfoHtml(op);

        let marker;

        if (useAdvancedMarkers) {
          const { AdvancedMarkerElement, PinElement } =
            window.google.maps.marker;
          const pin = new PinElement({
            background: statusColor,
            glyphColor: "#ffffff",
            borderColor: "#ffffff",
            scale: op.isOutsideBounds ? 1.0 : 1.2,
          });

          marker = new AdvancedMarkerElement({
            position,
            map: newMap,
            title: `${op.first_name} ${op.last_name} - ${op.status}`,
            content: pin,
          });

          marker.addEventListener("gmp-click", () => {
            infoWindowRef.current.setContent(infoContent);
            infoWindowRef.current.open({ anchor: marker, map: newMap });
          });
        } else {
          marker = new window.google.maps.Marker({
            position,
            map: newMap,
            title: `${op.first_name} ${op.last_name} - ${op.status}`,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: statusColor,
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          });

          marker.addListener("click", () => {
            infoWindowRef.current.setContent(infoContent);
            infoWindowRef.current.open(newMap, marker);
          });
        }

        markersRef.current.push(marker);
      });

      newMap.fitBounds(bounds);

      const listener = window.google.maps.event.addListener(
        newMap,
        "idle",
        () => {
          if (newMap.getZoom() > MAX_ZOOM_AFTER_FIT)
            newMap.setZoom(MAX_ZOOM_AFTER_FIT);
          window.google.maps.event.removeListener(listener);
        }
      );

      setMapMarkers(operatorsWithLocation);
    } catch (err) {
      console.error("Error initializing map:", err);
      setError("Failed to initialize map. Please try refreshing the page.");
    }
  }, [googleMapsLoaded, filteredOperators]);

  useEffect(() => {
    if (googleMapsLoaded && mapRef.current) initMap();
  }, [googleMapsLoaded, filteredOperators, initMap]);

  /* Cleanup markers on unmount */
  useEffect(() => {
    return () => {
      markersRef.current.forEach((m) => {
        if (m && typeof m.setMap === "function") m.setMap(null);
      });
      markersRef.current = [];
    };
  }, []);

  /* ================================================================ */
  /* HANDLERS                                                         */
  /* ================================================================ */

  const openDirections = (op) => {
    if (op?.latitude && op?.longitude) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${op.latitude},${op.longitude}`,
        "_blank"
      );
    }
  };

  const getStatusCounts = () => {
    const lower = (s) => (s || "").toLowerCase();
    return {
      All: operators.length,
      Active: operators.filter((op) => lower(op.status) === "active").length,
      Busy: operators.filter((op) => lower(op.status) === "busy").length,
      Inactive: operators.filter((op) => lower(op.status) === "inactive")
        .length,
      Offline: operators.filter((op) => lower(op.status) === "offline").length,
    };
  };

  const statusCounts = getStatusCounts();
  const operatorsWithLocation = filteredOperators.filter(
    (op) => op.hasLocation
  );
  const operatorsWithoutLocation = filteredOperators.filter(
    (op) => !op.hasLocation
  );

  const handleManualRefresh = debounce(() => {
    setRetryCount(0);
    fetchOperators();
  }, 1000);

  const handleFitAllMarkers = () => {
    if (!map || mapMarkers.length === 0 || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    mapMarkers.forEach((op) =>
      bounds.extend({ lat: op.latitude, lng: op.longitude })
    );
    map.fitBounds(bounds);
  };

  const handleCenterDefault = () => {
    if (!map) return;
    map.setCenter(DEFAULT_CENTER);
    map.setZoom(DEFAULT_ZOOM);
  };

  const handleForceLocationUpdate = async () => {
    const result = await window.Swal?.fire?.({
      title: "Force Location Update?",
      text: "This will request all operators to update their locations.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: Colors.PRIMARY,
      confirmButtonText: "Yes, proceed",
    });

    if (result && !result.isConfirmed) return;
    if (
      !window.Swal &&
      !window.confirm(
        "This will request all operators to update their locations. Proceed?",
      )
    )
      return;

    try {
      await api.post("/admin/operators/force-location-update");
      fetchOperators();
    } catch (err) {
      setError(
        "Failed to force location update: " + (err.message || "Unknown error"),
      );
    }
  };

  /* ================================================================ */
  /* LOADING SKELETON                                                 */
  /* ================================================================ */

  if (loading && operators.length === 0) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>Location Tracking</h2>
            <p>Loading operator locations…</p>
          </div>
          <div className="bk-card" style={{ padding: 20 }}>
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bk-skel mb-3"
                style={{ height: 42 }}
              />
            ))}
          </div>
        </Container>
      </div>
    );
  }

  /* ================================================================ */
  /* MAIN RENDER                                                      */
  /* ================================================================ */

  return (
    <div className="bk-page">
      <Styles />
      <Container fluid>
        {/* ============ HEADER ============ */}
        <div className="bk-header">
          <div
            className="position-relative d-flex justify-content-between align-items-center flex-wrap gap-3"
            style={{ zIndex: 1 }}
          >
            <div>
              <h2>Location Tracking</h2>
              <p>Live operator positions across Rinconada, Camarines Sur</p>
            </div>
          </div>
        </div>

        {/* ============ ALERTS ============ */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <i className="bi bi-exclamation-triangle me-2" />
                {error}
              </div>
              <div className="d-flex gap-2">
                <Button
                  className="bk-btn-ghost"
                  size="sm"
                  onClick={handleManualRefresh}
                >
                  Retry
                </Button>
                <Button
                  className="bk-btn-ghost"
                  size="sm"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </Button>
              </div>
            </div>
          </Alert>
        )}

        {retryStatus && (
          <Alert variant="info">
            <Spinner as="span" animation="border" size="sm" className="me-2" />
            {retryStatus}
          </Alert>
        )}

        {staleLocationAlert && (
          <Alert
            variant="warning"
            dismissible
            onClose={() => setStaleLocationAlert(false)}
          >
            <i className="bi bi-exclamation-triangle me-2" />
            <strong>Stale Location Data — </strong>
            Some operator locations are older than 24 hours. Consider forcing a
            location update.
          </Alert>
        )}

        {/* ============ TOOLBAR ============ */}
        <div className="bk-toolbar">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={4}>
              <InputGroup>
                <InputGroup.Text className="bk-input-icon">
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <FormControl
                  className="bk-input"
                  placeholder="Search name, email, or phone…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col xs={12} md={3}>
              <Dropdown>
                <Dropdown.Toggle className="bk-filter-btn w-100">
                  <i className="bi bi-funnel me-2" />
                  {statusFilter === "All"
                    ? `All Operators (${statusCounts.All})`
                    : `${statusFilter} (${statusCounts[statusFilter] || 0})`}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {STATUS_FILTER_OPTIONS.map((s) => (
                    <Dropdown.Item
                      key={s}
                      active={statusFilter === s}
                      onClick={() => setStatusFilter(s)}
                    >
                      {s === "All"
                        ? `All Operators (${statusCounts.All})`
                        : `${s} (${statusCounts[s]})`}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={12} md={5}>
              <div className="d-flex gap-2 flex-wrap justify-content-md-end">
                <Button
                  className="bk-btn-ghost"
                  onClick={() => setShowOffline(!showOffline)}
                >
                  <i
                    className={`bi ${
                      showOffline ? "bi-eye-slash" : "bi-eye"
                    } me-1`}
                  />
                  {showOffline ? "Hide Offline" : "Show Offline"}
                </Button>
                <Button
                  className="bk-btn-ghost"
                  onClick={() => setShowWithoutLocation(!showWithoutLocation)}
                >
                  <i
                    className={`bi ${
                      showWithoutLocation ? "bi-geo-alt-fill" : "bi-geo-alt"
                    } me-1`}
                  />
                  {showWithoutLocation
                    ? "Hide No-Location"
                    : "Show No-Location"}
                </Button>
              </div>
            </Col>
          </Row>

          {/* Status summary + map controls */}
          <div
            className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-3"
            style={{ borderTop: T.border }}
          >
            <small className="text-muted">
              Showing <strong>{filteredOperators.length}</strong> of{" "}
              <strong>{operators.length}</strong> operators ·{" "}
              <strong>{operatorsWithLocation.length}</strong> on map ·{" "}
              <strong>{operatorsWithoutLocation.length}</strong> without
              location
              {lastFetchTime && (
                <> · Last updated: {lastFetchTime.toLocaleTimeString()}</>
              )}
            </small>

            <div className="d-flex gap-2 flex-wrap">
              <Button
                className="bk-icon-btn"
                title="Refresh locations"
                onClick={handleManualRefresh}
              >
                <i
                  className={`bi bi-arrow-clockwise ${loading ? "spin" : ""}`}
                />
              </Button>
              <Button
                className="bk-icon-btn"
                title="Fit all markers"
                onClick={handleFitAllMarkers}
              >
                <i className="bi bi-bounding-box" />
              </Button>
              <Button
                className="bk-icon-btn"
                title="Reset to default center"
                onClick={handleCenterDefault}
              >
                <i className="bi bi-crosshair" />
              </Button>
              <Button
                className="bk-icon-btn"
                title="Force location update"
                onClick={handleForceLocationUpdate}
              >
                <i className="bi bi-geo-alt" />
              </Button>
              <Button
                className={`bk-icon-btn ${autoRefresh ? "active" : ""}`}
                title={
                  autoRefresh
                    ? "Auto refresh enabled"
                    : "Auto refresh disabled"
                }
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                <i
                  className={`bi ${
                    autoRefresh ? "bi-broadcast" : "bi-broadcast-pin"
                  }`}
                />
              </Button>
            </div>
          </div>
        </div>

        {/* ============ MAP ============ */}
        <div className="bk-map-wrap mb-3">
          <div ref={mapRef} style={MAP_CONTAINER_STYLE} />

          {!googleMapsLoaded && (
            <div className="bk-map-placeholder">
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted mb-0">Loading map…</p>
              </div>
            </div>
          )}

          {googleMapsLoaded && operatorsWithLocation.length === 0 && (
            <div className="bk-map-overlay">
              <small className="text-muted">
                <i className="bi bi-info-circle me-2" />
                No operators with location data match your filters.
              </small>
            </div>
          )}
        </div>

        {/* ============ LEGEND ============ */}
        <div className="d-flex flex-wrap gap-2 mb-4">
          {LEGEND_ITEMS.map((s) => (
            <span key={s} className="bk-legend-item">
              <span
                className="bk-legend-dot"
                style={{ background: MARKER_COLORS[s] }}
              />
              {s}
            </span>
          ))}
          <span className="bk-legend-item" style={{ color: Colors.WARNING }}>
            <i className="bi bi-exclamation-triangle" /> Stale (&gt;24h)
          </span>
          <span className="bk-legend-item" style={{ color: Colors.RED }}>
            <i className="bi bi-geo-alt-fill" /> Outside bounds
          </span>
        </div>

        {/* ============ STATUS FILTER CHIPS ============ */}
        <div className="d-flex flex-wrap gap-2 mb-4">
          {STATUS_FILTER_OPTIONS.map((key) => (
            <span
              key={key}
              className={`bk-chip ${statusFilter === key ? "active" : ""}`}
              onClick={() => setStatusFilter(key)}
            >
              {key}: {statusCounts[key]}
            </span>
          ))}
        </div>

        {/* ============ OPERATORS WITHOUT LOCATION ============ */}
        {operatorsWithoutLocation.length > 0 && (
          <div className="bk-card mb-4">
            <div className="bk-card-header">
              <h5>
                <i
                  className="bi bi-geo-alt"
                  style={{ color: Colors.WARNING }}
                />
                Operators Without Location Data
                <Badge bg="warning" text="dark">
                  {operatorsWithoutLocation.length}
                </Badge>
              </h5>
              <small>
                These operators have not yet reported their GPS coordinates
              </small>
            </div>
            <div style={{ padding: 20 }}>
              <Row className="g-3">
                {operatorsWithoutLocation.map((op) => (
                  <Col xs={12} md={6} lg={4} key={op.operator_id}>
                    <div className="bk-operator-card">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <OperatorAvatar operator={op} />
                          <div>
                            <div className="bk-operator-name">
                              {op.first_name} {op.last_name}
                            </div>
                            <div className="bk-operator-meta">
                              #{op.operator_id}
                            </div>
                          </div>
                        </div>
                        <StatusPill status={op.status} />
                      </div>

                      {op.email && (
                        <div className="bk-operator-meta">
                          <i className="bi bi-envelope me-1" />
                          {op.email}
                        </div>
                      )}
                      {op.phone && (
                        <div className="bk-operator-meta">
                          <i className="bi bi-telephone me-1" />
                          {op.phone}
                        </div>
                      )}
                      {op.location && (
                        <div className="bk-operator-meta">
                          <i className="bi bi-geo me-1" />
                          {op.location}
                        </div>
                      )}

                      <Alert
                        variant="warning"
                        className="mt-3 mb-0 py-2 px-3"
                        style={{ fontSize: 12 }}
                      >
                        <i className="bi bi-exclamation-triangle me-1" />
                        No GPS coordinates available
                      </Alert>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        )}

        {/* ============ OPERATORS WITH LOCATION ============ */}
        <div className="bk-card">
          <div className="bk-card-header">
            <h5>
              <i
                className="bi bi-geo-alt-fill"
                style={{ color: Colors.SUCCESS }}
              />
              Operators With Location
              <Badge bg="success">{operatorsWithLocation.length}</Badge>
            </h5>
            <small>Live positions from GPS reporting</small>
          </div>

          <div style={{ padding: 20 }}>
            {operatorsWithLocation.length === 0 ? (
              <div className="bk-empty">
                <div className="bk-empty-icon">
                  <i className="bi bi-geo" />
                </div>
                <h5 style={{ color: Colors.TEXT_DARK }}>
                  No operators with location
                </h5>
                <p className="text-muted mb-0">
                  No operators have reported GPS coordinates for the current
                  filters.
                </p>
              </div>
            ) : (
              <Row className="g-3">
                {operatorsWithLocation.map((op) => (
                  <Col xs={12} md={6} lg={4} key={op.operator_id}>
                    <div className="bk-operator-card">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <OperatorAvatar operator={op} />
                          <div>
                            <div className="bk-operator-name">
                              {op.first_name} {op.last_name}
                            </div>
                            <div className="bk-operator-meta">
                              #{op.operator_id}
                            </div>
                          </div>
                        </div>
                        <StatusPill status={op.status} />
                      </div>

                      {op.email && (
                        <div className="bk-operator-meta">
                          <i className="bi bi-envelope me-1" />
                          {op.email}
                        </div>
                      )}
                      {op.phone && (
                        <div className="bk-operator-meta">
                          <i className="bi bi-telephone me-1" />
                          {op.phone}
                        </div>
                      )}

                      <div
                        className="bk-operator-meta"
                        style={{ fontFamily: "ui-monospace, monospace" }}
                      >
                        <i className="bi bi-crosshair me-1" />
                        {op.latitude.toFixed(5)}, {op.longitude.toFixed(5)}
                      </div>

                      {op.last_location_update && (
                        <div className="bk-operator-meta">
                          <i className="bi bi-clock-history me-1" />
                          {new Date(op.last_location_update).toLocaleString()}
                        </div>
                      )}

                      <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
                        <Button
                          className="bk-btn-ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedForDirections(op);
                            setMapDialogOpen(true);
                          }}
                        >
                          <i className="bi bi-signpost-split me-1" />
                          Get Directions
                        </Button>

                        {op.isStale && (
                          <WarningPill
                            icon="bi-exclamation-triangle"
                            label="Stale"
                            color={Colors.WARNING}
                          />
                        )}
                        {op.isOutsideBounds && (
                          <WarningPill
                            icon="bi-geo-alt-fill"
                            label="Outside bounds"
                            color={Colors.RED}
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </div>

        {/* ============ DIRECTIONS MODAL ============ */}
        <Modal
          show={mapDialogOpen}
          onHide={() => setMapDialogOpen(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.PRIMARY}1f`,
                    color: Colors.PRIMARY,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i className="bi bi-signpost-split" />
                </span>
                Get Directions
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p className="text-muted">
              Open Google Maps with directions to{" "}
              <strong style={{ color: Colors.TEXT_DARK }}>
                {selectedForDirections?.first_name}{" "}
                {selectedForDirections?.last_name}
              </strong>
              's current location.
            </p>

            {selectedForDirections?.vehicle_reg && (
              <div className="bk-operator-meta">
                <strong>Vehicle:</strong> {selectedForDirections.vehicle_reg}
              </div>
            )}

            {selectedForDirections?.isStale && (
              <Alert variant="warning" className="mt-3 mb-0">
                <i className="bi bi-exclamation-triangle me-2" />
                Location data may be stale (older than 24h).
              </Alert>
            )}

            {selectedForDirections?.isOutsideBounds && (
              <Alert variant="danger" className="mt-3 mb-0">
                <i className="bi bi-geo-alt-fill me-2" />
                This operator is outside the Rinconada bounds.
              </Alert>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              className="bk-btn-ghost"
              onClick={() => setMapDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="bk-btn-primary"
              onClick={() => {
                openDirections(selectedForDirections);
                setMapDialogOpen(false);
              }}
            >
              <i className="bi bi-box-arrow-up-right me-1" />
              Open Directions
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default LocationTracking;